import { useState, useEffect } from 'react';
import { TrendingUp, ChevronDown, ChevronUp, Clock, CheckCircle } from 'lucide-react';
import ConfirmDialog from '@/components/shared/ConfirmDialog';
import { toast } from 'sonner';
import { apiGet, apiPost } from '@/lib/api';
import { formatDate, parseApiDate, formatBandPeriodLabel, formatBandDateRangeLabel } from '@/lib/formatters';
import { PRICING_VERSION } from '@/lib/offerPricing';
import '@/styles/authenticated.css';


const fmt = (v) => {
  if (!v && v !== 0) return '-';
  return parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Accepts ISO yyyy-mm-dd or a full timestamp; renders Eastern, as "Nov 16, 2026".
// formatDate handles both shapes — including the offset-less timestamps the API
// sends, which the local `new Date(...)` here read as browser-local time.
const fmtDate = (dateStr) => formatDate(dateStr) || '-';

const monthsFromNow = (dateStr) => {
  const d = parseApiDate(dateStr);
  if (!d) return '';
  const months = Math.max(0, Math.round(((d - new Date()) / (1000 * 60 * 60 * 24 * 30.44))));
  return `${months} mo${months === 1 ? '' : 's'}`;
};

/**
 * The fees a plaintiff pays up front on an offer, as ONE number.
 *
 * Prefer the stored `total_fees`: the server computes it once, through
 * offer_pricing.resolve_fees, and `total_funded_amount` is literally
 * `advance + total_fees`. Re-summing the components here is what broke the
 * flat fee model — the funder's fee lives in `processing_fee` under that
 * model, and three separate call sites added broker + application + platform
 * and silently dropped it. On a $10,000 advance with a $250 processing fee
 * the comparison row read "Cash To You $10,000 | Upfront Fees $175 |
 * Starting Balance $10,425", three numbers that do not add up, and the
 * accept dialog — the last screen before an irreversible accept — said $175.
 *
 * The component sum stays only as a fallback for a document written before
 * total_fees existed, and it now includes processing_fee.
 */
const upfrontFees = (offer) => {
  if (typeof offer?.total_fees === 'number') return offer.total_fees;
  return (offer?.broker_fee || 0) + (offer?.application_fee || 0)
    + (offer?.processing_fee || 0) + (offer?.platform_fee || 0);
};

/** Cash + upfront fees. Stored as total_funded_amount; derived if absent. */
const startingBalanceOf = (offer) => (
  typeof offer?.total_funded_amount === 'number'
    ? offer.total_funded_amount
    : (offer?.advance_amount || 0) + upfrontFees(offer)
);

const riskBadge = (tier) => {
  const map = {
    low: { bg: '#dcfce7', color: '#166534', label: 'Low' },
    medium: { bg: '#fef9c3', color: '#854d0e', label: 'Medium' },
    high: { bg: '#fee2e2', color: '#991b1b', label: 'High' },
  };
  const s = map[tier] || map.medium;
  return <span style={{ padding: '2px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, background: s.bg, color: s.color }}>{s.label}</span>;
};

const CompareOffers = ({ applicationId, onOfferAccepted, canAccept = false, showRisk = false }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSchedule, setExpandedSchedule] = useState(null);
  const [feeBreakdownOpen, setFeeBreakdownOpen] = useState(false);
  const [confirmOffer, setConfirmOffer] = useState(null);
  const [accepting, setAccepting] = useState(false);

  const fetchOffers = async () => {
    setLoading(true);
    try {
      setOffers(await apiGet(`/applications/${applicationId}/offers`));
    } catch (e) { console.error('Failed to fetch offers:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (applicationId) fetchOffers(); }, [applicationId]);

  const handleAcceptOffer = async () => {
    if (!confirmOffer) return;
    setAccepting(true);
    try {
      await apiPost(`/offers/${confirmOffer.offer_id}/accept`);
      toast.success('Offer accepted! This loan is now active.');
      setConfirmOffer(null);
      fetchOffers();
      if (onOfferAccepted) onOfferAccepted();
    } catch (e) { toast.error(e.message || 'Failed to accept offer'); }
    finally { setAccepting(false); }
  };

  if (loading) return (
    <div className="mcl-card" style={{ marginBottom: '1.25rem' }}>
      <div className="mcl-card-body" style={{ textAlign: 'center', padding: '2rem', color: 'var(--mcl-text-muted)' }}>Loading offers...</div>
    </div>
  );

  if (!offers.length) return null;

  // An offer's pricing version, not just whatever it happens to carry: an
  // older offer has no `pricing_version` at all, so anything short of the
  // current version reads as v1 rather than as some other v2 variant.
  const versionOf = (offer) => (offer.pricing_version === PRICING_VERSION ? 2 : 1);
  // With identical inputs a v1 and a v2 offer are numerically identical at
  // every month they share — the only place they diverge is settling BETWEEN
  // rows, which a comparison table never shows. The banner exists to say so,
  // and only when the list actually mixes the two: it costs nothing in the
  // steady state and disappears on its own as v1 offers age out.
  const mixedPricing = new Set(offers.map(versionOf)).size > 1;

  const hasAccepted = offers.some(o => o.status === 'accepted');
  const bestAdvance = Math.max(...offers.map(o => o.advance_amount || 0));
  const bestRate = Math.min(...offers.map(o => o.annualized_rate || 100));
  const bestCap = Math.min(...offers.map(o => o.maximum_cap || Infinity));

  return (
    <div data-testid="compare-offers-section" style={{ marginBottom: '1.25rem' }}>
      <div className="mcl-card" style={{ borderColor: hasAccepted ? '#a7f3d0' : '#93c5fd', overflow: 'visible' }}>
        <div className="mcl-card-header" style={{ background: hasAccepted ? 'linear-gradient(135deg, #f0fdf4, #dcfce7)' : 'linear-gradient(135deg, #eff6ff, #dbeafe)', borderBottomColor: hasAccepted ? '#a7f3d0' : '#93c5fd' }}>
          <h3 className="mcl-card-header-title" style={{ color: hasAccepted ? '#065f46' : '#1e40af', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            {hasAccepted ? <><CheckCircle size={18} /> Accepted Offer</> : <><TrendingUp size={18} /> Compare Funding Offers ({offers.length})</>}
          </h3>
        </div>
        <div className="mcl-card-body-flush">
          {mixedPricing && (
            <div
              data-testid="mixed-pricing-banner"
              style={{
                margin: '1rem 1.25rem 0', padding: '0.85rem 1rem',
                background: 'var(--mcl-warning-bg)', border: '1px solid var(--mcl-warning)',
                borderRadius: '8px', fontSize: '0.82rem', color: 'var(--mcl-text-body)', lineHeight: 1.5,
              }}
            >
              <strong style={{ color: 'var(--mcl-warning)' }}>These offers price differently.</strong>{' '}
              One sets a repayment amount on each date; the other sets one amount for an entire
              window of time. If your case settles between two rows, that difference matters —
              review each offer&apos;s own schedule below rather than comparing dates side by side.
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table className="mcl-table" data-testid="compare-offers-table">
              <thead>
                <tr>
                  <th style={{ minWidth: '140px' }}>Funder</th>
                  <th style={{ textAlign: 'right', minWidth: '120px' }}>
                    Cash To You
                    <span title="The actual amount deposited into your account if you accept this offer." style={{ marginLeft: '4px', cursor: 'help', color: 'var(--mcl-text-muted)', fontSize: '0.75rem' }}>ⓘ</span>
                  </th>
                  <th style={{ textAlign: 'right', minWidth: '110px' }}>
                    Upfront Fees
                    <span title="One-time fees rolled into your balance at closing. Open the schedule below to see the breakdown." style={{ marginLeft: '4px', cursor: 'help', color: 'var(--mcl-text-muted)', fontSize: '0.75rem' }}>ⓘ</span>
                  </th>
                  <th style={{ textAlign: 'right', minWidth: '140px' }}>
                    Starting Balance
                    <span title="Your starting balance = cash you receive + upfront fees. Interest is calculated on this amount, not just the cash advance." style={{ marginLeft: '4px', cursor: 'help', color: 'var(--mcl-text-muted)', fontSize: '0.75rem' }}>ⓘ</span>
                  </th>
                  <th style={{ textAlign: 'center', minWidth: '80px' }}>
                    Rate
                    <span title="Annualized rate applied to your starting balance. Non-recourse: you only repay if your case settles." style={{ marginLeft: '4px', cursor: 'help', color: 'var(--mcl-text-muted)', fontSize: '0.75rem' }}>ⓘ</span>
                  </th>
                  <th style={{ textAlign: 'right', minWidth: '120px' }}>
                    Max You&apos;d Ever Owe
                    <span title="The absolute ceiling on your repayment, no matter how long your case takes." style={{ marginLeft: '4px', cursor: 'help', color: 'var(--mcl-text-muted)', fontSize: '0.75rem' }}>ⓘ</span>
                  </th>
                  <th style={{ textAlign: 'center', minWidth: '80px' }}>Term</th>
                  {showRisk && <th style={{ textAlign: 'center', minWidth: '80px' }}>Risk</th>}
                  <th style={{ textAlign: 'center', minWidth: '100px' }}>Schedule</th>
                  <th style={{ textAlign: 'center', minWidth: '100px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {offers.map((offer, idx) => {
                  const isBestAdvance = offer.advance_amount === bestAdvance;
                  const isBestRate = offer.annualized_rate === bestRate;
                  const isBestCap = offer.maximum_cap === bestCap;
                  const isAccepted = offer.status === 'accepted';
                  const isDeclined = offer.status === 'declined';

                  return (
                    <tr key={offer.offer_id || idx} data-testid={`offer-row-${idx}`} style={isAccepted ? { backgroundColor: '#f0fdf4' } : isDeclined ? { opacity: 0.5 } : {}}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                          <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)', fontSize: '0.875rem' }}>{offer.funder_company || 'Funder'}</div>
                          {mixedPricing && (
                            <span
                              data-testid="offer-version-tag"
                              title={versionOf(offer) === 2 ? 'Priced under the current payment-window schedule' : 'Priced under the earlier per-date schedule'}
                              style={{ fontSize: '0.65rem', fontWeight: 600, padding: '2px 6px', background: 'var(--mcl-info-bg)', color: 'var(--mcl-info)', borderRadius: '999px', whiteSpace: 'nowrap', letterSpacing: '0.02em' }}
                            >
                              v{versionOf(offer)} pricing
                            </span>
                          )}
                          {offer.revision_count > 1 && (
                            <span
                              data-testid={`offer-revised-badge-${idx}`}
                              title={`Newer offer than the one submitted on ${formatDate(offer.original_submitted_at)}`}
                              style={{ fontSize: '0.65rem', fontWeight: 600, padding: '2px 6px', background: '#fef3c7', color: '#92400e', borderRadius: '999px', whiteSpace: 'nowrap', letterSpacing: '0.02em' }}
                            >
                              REVISED
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--mcl-text-muted)' }}>
                          <Clock size={10} style={{ marginRight: '3px', verticalAlign: 'middle' }} />
                          {offer.submitted_at ? formatDate(offer.submitted_at) : '-'}
                          {offer.revision_count > 1 && (
                            <span style={{ marginLeft: '0.4rem', color: '#92400e' }}>
                              · newer than {formatDate(offer.original_submitted_at)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }} data-testid={`offer-advance-${idx}`}>
                        <span style={{ fontWeight: 600, color: isBestAdvance && offers.length > 1 ? '#166534' : 'var(--mcl-text-heading)' }}>
                          ${fmt(offer.advance_amount)}
                        </span>
                        {isBestAdvance && offers.length > 1 && !hasAccepted && <div style={{ fontSize: '0.65rem', color: '#166534', fontWeight: 600 }}>BEST</div>}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 500, color: 'var(--mcl-text-muted)' }} data-testid={`offer-fees-${idx}`}>
                        ${fmt(upfrontFees(offer))}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 500 }} data-testid={`offer-starting-balance-${idx}`}>${fmt(startingBalanceOf(offer))}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ fontWeight: 600, color: isBestRate && offers.length > 1 ? '#166534' : 'var(--mcl-text-heading)' }}>
                          {offer.annualized_rate}%
                        </span>
                        {isBestRate && offers.length > 1 && !hasAccepted && <div style={{ fontSize: '0.65rem', color: '#166534', fontWeight: 600 }}>BEST</div>}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span style={{ fontWeight: 500, color: isBestCap && offers.length > 1 ? '#166534' : 'var(--mcl-text-heading)' }}>
                          ${fmt(offer.maximum_cap)}
                        </span>
                        {isBestCap && offers.length > 1 && !hasAccepted && <div style={{ fontSize: '0.65rem', color: '#166534', fontWeight: 600 }}>BEST</div>}
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 500 }}>{offer.max_term_months || '-'}mo</td>
                      {showRisk && <td style={{ textAlign: 'center' }}>{riskBadge(offer.risk_tier)}</td>}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          onClick={() => setExpandedSchedule(expandedSchedule === idx ? null : idx)}
                          style={{ background: 'none', border: '1px solid var(--mcl-border)', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '0.75rem', color: 'var(--mcl-primary)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                          data-testid={`toggle-schedule-${idx}`}
                        >
                          {expandedSchedule === idx ? <><ChevronUp size={12} /> Hide</> : <><ChevronDown size={12} /> View</>}
                        </button>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {isAccepted ? (
                          <span className="mcl-badge mcl-badge-success" style={{ fontSize: '0.7rem' }}>
                            <CheckCircle size={10} style={{ marginRight: '3px' }} /> Accepted
                          </span>
                        ) : isDeclined ? (
                          <span className="mcl-badge mcl-badge-muted" style={{ fontSize: '0.7rem' }}>Declined</span>
                        ) : !hasAccepted && canAccept ? (
                          <button
                            className="mcl-btn mcl-btn-sm mcl-btn-success"
                            onClick={() => setConfirmOffer(offer)}
                            data-testid={`accept-offer-${idx}`}
                            style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                          >
                            <CheckCircle size={12} /> Accept
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Expanded repayment schedule
              A v1 offer carries `repayment_schedule` (one amount pinned to a
              date); a v2 offer carries `repayment_bands` (one amount owed
              anywhere inside a window). Old offers are frozen and never
              converted, so both shapes render here, natively, indefinitely.
              Guarded so a v2 offer with a missing/empty `repayment_bands` —
              a data bug — degrades to "no schedule" for that offer instead
              of throwing and blanking the comparison for every other one. */}
          {expandedSchedule !== null && (() => {
            const o = offers[expandedSchedule];
            if (!o) return null;
            const isV2 = versionOf(o) === 2;
            const bands = o.repayment_bands;
            const hasSchedule = isV2 ? Array.isArray(bands) && bands.length > 0 : o.repayment_schedule?.length > 0;
            if (!hasSchedule) return null;
            const advance = o.advance_amount || 0;
            const brokerFee = o.broker_fee || 0;
            const appFee = o.application_fee || 0;
            const processingFee = o.processing_fee || 0;
            const platformFee = o.platform_fee || 0;
            // The headline reads the stored total, not a re-sum of the lines
            // below it, so cash + fees == starting balance holds under either
            // fee model. The itemised lines then render whichever components
            // this offer actually carries.
            const totalFees = upfrontFees(o);
            const startingBalance = startingBalanceOf(o);
            return (
            <div data-testid="expanded-schedule" style={{ borderTop: '2px solid var(--mcl-primary)', padding: '1.25rem', background: '#f8fafc' }}>
              {/* Fee summary panel — single "Fees" line by default; expand to itemize.
                  As more platform fees are added (e.g. platform fee), they slot
                  into the breakdown without changing the headline. */}
              <div style={{ background: '#fff', border: '1px solid var(--mcl-border)', borderRadius: '10px', padding: '1rem 1.25rem', marginBottom: '1rem' }} data-testid="fee-breakdown-panel">
                <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--mcl-text-muted)', marginBottom: '0.6rem' }}>
                  How your starting balance is built
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', alignItems: 'end' }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--mcl-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Cash to you</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#16a34a' }} data-testid="fee-breakdown-advance">${fmt(advance)}</div>
                  </div>
                  <div style={{ fontSize: '1.4rem', color: 'var(--mcl-text-light)', textAlign: 'center', alignSelf: 'center' }}>+</div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--mcl-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Fees</div>
                    <button
                      type="button"
                      onClick={() => setFeeBreakdownOpen(v => !v)}
                      data-testid="fee-breakdown-toggle"
                      aria-expanded={feeBreakdownOpen}
                      title={feeBreakdownOpen ? 'Hide fee breakdown' : 'Show fee breakdown'}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                        padding: 0, background: 'transparent', border: 'none', cursor: 'pointer',
                        fontSize: '1.1rem', fontWeight: 700, color: 'var(--mcl-text-heading)',
                      }}
                    >
                      ${fmt(totalFees)}
                      {feeBreakdownOpen
                        ? <ChevronUp size={14} style={{ color: 'var(--mcl-text-muted)' }} />
                        : <ChevronDown size={14} style={{ color: 'var(--mcl-text-muted)' }} />
                      }
                    </button>
                  </div>
                  <div style={{ fontSize: '1.4rem', color: 'var(--mcl-text-light)', textAlign: 'center', alignSelf: 'center' }}>=</div>
                  <div style={{ background: '#eff6ff', padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid #93c5fd' }}>
                    <div style={{ fontSize: '0.7rem', color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>Starting balance</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e40af' }} data-testid="fee-breakdown-total">${fmt(startingBalance)}</div>
                  </div>
                </div>

                {feeBreakdownOpen && (
                  <div data-testid="fee-breakdown-details" style={{ marginTop: '0.9rem', padding: '0.75rem 0.9rem', background: '#f8fafc', border: '1px solid var(--mcl-border)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--mcl-text-muted)', marginBottom: '0.4rem' }}>Fee breakdown</div>
                    {/* A funder charges EITHER a broker + application pair OR a
                        single flat processing fee, so the line that does not
                        apply is $0 and is left out rather than printed as a
                        zero the plaintiff has to interpret. Under the flat
                        model the processing fee is the funder's whole fee —
                        omitting it was how $250 disappeared from this panel. */}
                    {brokerFee > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', padding: '0.2rem 0' }}>
                        <span style={{ color: 'var(--mcl-text-body)' }}>Broker fee</span>
                        <span style={{ fontWeight: 600 }} data-testid="fee-breakdown-broker">${fmt(brokerFee)}</span>
                      </div>
                    )}
                    {appFee > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', padding: '0.2rem 0' }}>
                        <span style={{ color: 'var(--mcl-text-body)' }}>Application fee</span>
                        <span style={{ fontWeight: 600 }} data-testid="fee-breakdown-app">${fmt(appFee)}</span>
                      </div>
                    )}
                    {processingFee > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', padding: '0.2rem 0' }}>
                        <span style={{ color: 'var(--mcl-text-body)' }}>Processing fee</span>
                        <span style={{ fontWeight: 600 }} data-testid="fee-breakdown-processing">${fmt(processingFee)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', padding: '0.2rem 0' }}>
                      <span style={{ color: 'var(--mcl-text-body)' }}>MyCaseLoan platform fee</span>
                      <span style={{ fontWeight: 600 }} data-testid="fee-breakdown-platform">${fmt(platformFee)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', padding: '0.4rem 0 0', borderTop: '1px solid var(--mcl-border)', marginTop: '0.4rem' }}>
                      <span style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>Total fees</span>
                      <span style={{ fontWeight: 700 }} data-testid="fee-breakdown-total-fees">${fmt(totalFees)}</span>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '0.7rem', fontSize: '0.78rem', color: 'var(--mcl-text-muted)', lineHeight: 1.5 }}>
                  Your repayment grows from <strong>${fmt(startingBalance)}</strong> at <strong>{o.annualized_rate}%</strong> per year and is capped at <strong>${fmt(o.maximum_cap)}</strong>. You only pay if your case settles.
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: 'var(--mcl-text-heading)' }}>
                  Repayment Schedule — {o.funder_company || 'Funder'}
                </h4>
                <span style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>
                  Rate: {o.annualized_rate}% &nbsp;|&nbsp; Cap: ${fmt(o.maximum_cap)}
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                {isV2 ? (
                  // v2: a band is one amount owed anywhere inside a window, not
                  // pinned to a date, so the period leads and the exact dates sit
                  // beneath it — same label logic as the funder's offer builder
                  // (frontend/src/lib/formatters.js), reused rather than
                  // recomputed. The badge follows `is_cap`, never `is_terminal`:
                  // every schedule's last band is terminal, but only some are
                  // terminal because the cap ended it.
                  <table className="mcl-table" style={{ fontSize: '0.8125rem' }} data-testid="repayment-schedule-table">
                    <thead>
                      <tr>
                        <th>If Your Case Settles</th>
                        <th style={{ textAlign: 'center' }}>Window Length</th>
                        <th style={{ textAlign: 'right' }}>Total Repayment</th>
                        <th style={{ textAlign: 'right' }}>
                          Cost of Funding
                          <span title="Total Repayment minus the cash you received. This is what the funding actually costs you." style={{ marginLeft: '4px', cursor: 'help', color: 'var(--mcl-text-muted)', fontSize: '0.75rem' }}>ⓘ</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {bands.map((band, i) => {
                        const cost = (band.amount || 0) - advance;
                        return (
                          <tr key={i} data-testid={`band-row-${i}`} style={band.is_cap ? { backgroundColor: 'var(--mcl-warning-bg)' } : {}}>
                            <td>
                              <div data-testid="band-period" style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>
                                {formatBandPeriodLabel(band.from_month, band.to_month)}
                              </div>
                              <div data-testid="band-dates" style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)', marginTop: '0.1rem' }}>
                                {formatBandDateRangeLabel(band.from, band.to)}
                                {band.is_cap && <span style={{ marginLeft: '0.4rem', color: 'var(--mcl-warning)', fontWeight: 600 }}>(Final Cap)</span>}
                              </div>
                            </td>
                            <td style={{ textAlign: 'center', color: 'var(--mcl-text-muted)' }}>{band.to_month - band.from_month} mo</td>
                            <td data-testid="band-amount" style={{ textAlign: 'right', fontWeight: 600 }}>${fmt(band.amount)}</td>
                            <td style={{ textAlign: 'right', color: 'var(--mcl-danger, #dc2626)', fontWeight: 500 }}>${fmt(cost)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <table className="mcl-table" style={{ fontSize: '0.8125rem' }} data-testid="repayment-schedule-table">
                    <thead>
                      <tr>
                        <th>If Your Case Settles By</th>
                        <th style={{ textAlign: 'center' }}>From Now</th>
                        <th style={{ textAlign: 'right' }}>Total Repayment</th>
                        <th style={{ textAlign: 'right' }}>
                          Cost of Funding
                          <span title="Total Repayment minus the cash you received. This is what the funding actually costs you." style={{ marginLeft: '4px', cursor: 'help', color: 'var(--mcl-text-muted)', fontSize: '0.75rem' }}>ⓘ</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {o.repayment_schedule.map((row, i) => {
                        const cost = (row.amount || 0) - advance;
                        return (
                          <tr key={i} style={row.is_cap ? { backgroundColor: 'var(--mcl-warning-bg)' } : {}}>
                            <td>{fmtDate(row.date)}{row.is_cap && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: 'var(--mcl-warning)', fontWeight: 600 }}>(Final Cap)</span>}</td>
                            <td style={{ textAlign: 'center', color: 'var(--mcl-text-muted)' }}>{monthsFromNow(row.date)}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>${fmt(row.amount)}</td>
                            <td style={{ textAlign: 'right', color: 'var(--mcl-danger, #dc2626)', fontWeight: 500 }}>${fmt(cost)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            );
          })()}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmDialog
        open={!!confirmOffer}
        onOpenChange={(open) => { if (!open) setConfirmOffer(null); }}
        tone="success"
        title="Accept This Funding Offer?"
        description="You are about to accept the following offer:"
        warning="This cannot be undone. All other pending offers will be automatically declined."
        confirmLabel="Yes, Accept Offer"
        busyLabel="Accepting..."
        busy={accepting}
        onConfirm={handleAcceptOffer}
        testId="confirm-accept-offer"
      >
        {/* Rich summary lives here rather than in the description, which Radix
            renders as a <p> — these <div>s made it invalid HTML. */}
        {confirmOffer && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.875rem' }}>
              <div><span style={{ color: '#065f46' }}>Funder:</span> <strong>{confirmOffer.funder_company || 'Funder'}</strong></div>
              <div><span style={{ color: '#065f46' }}>Cash to you:</span> <strong style={{ color: '#16a34a' }}>${fmt(confirmOffer.advance_amount)}</strong></div>
              <div data-testid="confirm-upfront-fees"><span style={{ color: '#065f46' }}>Upfront fees:</span> <strong>${fmt(upfrontFees(confirmOffer))}</strong></div>
              <div data-testid="confirm-starting-balance"><span style={{ color: '#065f46' }}>Starting balance:</span> <strong>${fmt(startingBalanceOf(confirmOffer))}</strong></div>
              <div><span style={{ color: '#065f46' }}>Rate:</span> <strong>{confirmOffer.annualized_rate}%</strong></div>
              <div><span style={{ color: '#065f46' }}>Max you&apos;d ever owe:</span> <strong>${fmt(confirmOffer.maximum_cap)}</strong></div>
            </div>
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
};

export default CompareOffers;
